import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Flex,
  FormContainer,
  Icon,
  IconButton,
  Modal,
  Text,
  Title,
} from '@wyz/ui';
import { useBoolean } from '@wyz/utils';
import { isNil, round } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { NumberField } from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { QuoteHelpers } from '~/purchase/helpers';
import { itemPriceEditFormSchema, SERVICE_TYPE } from '~/purchase/schemas';
import {
  IQuoteCartAction,
  IQuoteCartGroup,
  IQuoteCartItemPriceEditFrom,
} from '~/purchase/types';
import { QuotePriceEditInput } from '~/purchase/components';

type Props = {
  group: IQuoteCartGroup;
  vatPercentage: number;
  onClose: () => void;
  actions: IQuoteCartAction;
};

function EditItemPriceModal({ group, vatPercentage, onClose, actions }: Props) {
  const { t } = useI18n();
  const locale = useLocale();
  const mountingService = group.services.find(
    (service) => service.serviceType === SERVICE_TYPE.MOUNTING,
  );
  const methods = useForm<IQuoteCartItemPriceEditFrom>({
    resolver: zodResolver(itemPriceEditFormSchema),
    defaultValues: {
      items: group.items.reduce<IQuoteCartItemPriceEditFrom['items']>(
        (values, item) => ({
          ...values,
          [item.id]: QuoteHelpers.getQuotePriceFieldValues(
            item.unitPriceTaxIncl,
            item.tyre.bf,
            item.buyingPrice,
            vatPercentage,
            item.quantity,
            'MARGIN',
          ),
        }),
        {},
      ),
      mountingPrice: mountingService
        ? round(mountingService.priceTaxIncl, 2)
        : undefined,
    },
  });

  const [editMountingPrice, editMountingPriceHandlers] = useBoolean(false);

  const onSubmit = (data: IQuoteCartItemPriceEditFrom) => {
    if (data.mountingPrice && !mountingService) {
      actions.addService.process({
        serviceName: t('app.common.quote.mounting'),
        serviceType: SERVICE_TYPE.MOUNTING,
        basePriceTaxIncl: data.mountingPrice,
        priceTaxIncl: data.mountingPrice ?? 0,
        targetEans: group.items.map((item) => item.tyre.ean),
      });
    }

    actions.updatePrice.process({
      items: Object.entries(data.items).map(
        ([id, { discountedPriceTaxIncl, quantity }]) => ({
          id,
          price: discountedPriceTaxIncl / quantity,
        }),
      ),
      services: mountingService
        ? [{ id: mountingService.id, price: data.mountingPrice ?? 0 }]
        : [],
    });
  };

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' centered>
      <FormContainer
        id='edit-price-form'
        methods={methods}
        submitHandler={onSubmit}
      >
        <Modal.Header closeButton>
          <Title heading='lg' weight='bold' color='secondary'>
            <Trans i18nKey='app.purchase.quote_cart_page.price_edit.title' />
          </Title>
        </Modal.Header>
        <Modal.Body>
          {group.items.map((item) => {
            return (
              <div key={item.id} className='mb-4'>
                <Text heading='normal' color='secondary' weight='bold'>
                  {item.tyre.profile}
                </Text>
                <Flex justifyContent='between' alignItems='center'>
                  <div>
                    <Text
                      heading='sm'
                      weight='semi-bold'
                      color='secondary-gray'
                      className='mt-3'
                    >
                      <Trans
                        i18nKey='app.purchase.quote_cart_page.price_edit.base_price_for'
                        values={{ count: item.quantity }}
                      />
                    </Text>
                    <Flex alignItems='center' className='gap-1 mb-2'>
                      <Title heading='lg' weight='bold' color='tertiary-bold'>
                        <Trans
                          i18nKey='app.purchase.quote_search_page.product.price_with_taxes'
                          values={{
                            price: locale.formatters.money(
                              item.quantity * item.unitPriceTaxIncl,
                              {
                                currency: item.currency,
                              },
                            ),
                          }}
                        />
                      </Title>
                      {!isNil(item.tyre.bf) && (
                        <Text
                          color='secondary-gray'
                          weight='bold'
                          heading='xxs'
                        >
                          <Trans
                            i18nKey='app.purchase.quote_search_page.product.bf_discount'
                            values={{
                              value: -QuoteHelpers.calculateBfDiscount(
                                item.tyre.bf,
                                QuoteHelpers.getPriceTaxExcl(
                                  item.unitPriceTaxIncl,
                                  vatPercentage,
                                ),
                              ),
                            }}
                          />
                        </Text>
                      )}
                    </Flex>
                  </div>
                  <IconButton
                    id='delete-discount'
                    variant='tertiary-grey'
                    icon='trash'
                    onClick={() =>
                      methods.setValue(
                        `items.${item.id}`,
                        QuoteHelpers.getQuotePriceFieldValues(
                          item.baseUnitPriceTaxIncl,
                          item.tyre.bf,
                          item.buyingPrice,
                          vatPercentage,
                          item.quantity,
                          'MARGIN',
                        ),
                      )
                    }
                  >
                    <Trans i18nKey='app.purchase.quote_cart_page.price_edit.remove_discount' />
                  </IconButton>
                </Flex>
                <QuotePriceEditInput
                  baseName={`items.${item.id}`}
                  item={{
                    currency: item.currency,
                    quantity: item.quantity,
                    unitPriceTaxIncl: item.unitPriceTaxIncl,
                    discountBasePrice: item.tyre.bf,
                    marginBasePrice: item.buyingPrice,
                  }}
                  vatPercentage={vatPercentage}
                  adjustmentType='MARGIN'
                  itemType={'TYRE'}
                />
              </div>
            );
          })}
          <div>
            <Text heading='normal' color='secondary' weight='bold'>
              <Trans i18nKey='app.common.quote.mounting' />
            </Text>
            <Text
              heading='sm'
              weight='semi-bold'
              color='secondary-gray'
              className='mt-3'
            >
              <Trans
                i18nKey='app.purchase.quote_cart_page.price_edit.base_price_for'
                values={{
                  count: group.items.reduce(
                    (sum, item) => sum + item.quantity,
                    0,
                  ),
                }}
              />
            </Text>
            <Flex justifyContent='between' alignItems='center'>
              {editMountingPrice ? (
                <div className='w-25'>
                  <NumberField name='mountingPrice' withKnobs={false} />
                </div>
              ) : (
                <Flex className='gap-2' alignItems='center'>
                  <Title heading='lg' weight='bold' color='tertiary-bold'>
                    {isNil(mountingService)
                      ? '-'
                      : t(
                          'app.purchase.quote_search_page.product.price_with_taxes',
                          {
                            price: locale.formatters.money(
                              mountingService.priceTaxIncl,
                              {
                                currency: group.items.at(0)?.currency,
                              },
                            ),
                          },
                        )}
                  </Title>
                  <Icon
                    icon='pencil-line'
                    className='cursor-pointer'
                    onClick={editMountingPriceHandlers.setTrue}
                  />
                </Flex>
              )}
              {editMountingPrice && (
                <IconButton
                  id='delete-mounting-discount'
                  variant='tertiary-grey'
                  icon='trash'
                  onClick={() =>
                    methods.setValue(
                      'mountingPrice',
                      mountingService
                        ? round(mountingService.basePriceTaxIncl, 2)
                        : null,
                    )
                  }
                >
                  <Trans i18nKey='app.purchase.quote_cart_page.price_edit.remove_discount' />
                </IconButton>
              )}
            </Flex>
          </div>
          <Flex className='gap-3 mt-4'>
            <Button id='cancel' variant='secondary' onClick={onClose} fullWidth>
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              form='edit-price-form'
              id='submit'
              variant='primary'
              fullWidth
              onClick={methods.handleSubmit((data) => {
                onSubmit(data);
                onClose();
              })}
            >
              <Trans i18nKey='app.common.button.apply' />
            </Button>
          </Flex>
        </Modal.Body>
      </FormContainer>
    </Modal.Container>
  );
}

export default EditItemPriceModal;
